/**
 * Passport cards — the single source of look & feel for Passport stamps,
 * citizen badges, and levels, shared by the Citizen Passport (journey-web)
 * and the Business Evidence view (business-web) so both render identically
 * (BL-061).
 *
 * Pure presentation: no i18n and no data hooks. Consumers pass already-
 * translated labels and (for badges) the resolved icon node, and may wrap
 * each card in their own motion wrapper for stagger animation.
 */

import * as React from 'react';
import {
  Award,
  BadgeCheck,
  Briefcase,
  CircleCheck,
  ClipboardCheck,
  Crown,
  Flame,
  Footprints,
  Globe,
  HeartPulse,
  House,
  Lock,
  Medal,
  PartyPopper,
  PlaneLanding,
  Scale,
  Sparkles,
  Target,
  Trophy,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { Badge } from '../primitives/badge';
import { Card, CardContent } from '../primitives/card';
import { Progress } from '../primitives/progress';
import { cn } from '../lib/utils';

export type PassportRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

/**
 * Passport stamp + badge icons are stored as lucide icon NAMES in kebab-case
 * (e.g. "footprints", "clipboard-check", "scale") — NOT emojis. Map the names
 * the passport configs use to their lucide components so both portals render
 * the real glyph. (Explicit map rather than the full lucide `icons` record so
 * tree-shaking keeps the bundle small.) New config icons must be added here.
 */
const PASSPORT_ICONS: Record<string, LucideIcon> = {
  'check-circle': CircleCheck,
  'clipboard-check': ClipboardCheck,
  crown: Crown,
  flame: Flame,
  footprints: Footprints,
  globe: Globe,
  'heart-pulse': HeartPulse,
  home: House,
  medal: Medal,
  'party-popper': PartyPopper,
  'plane-landing': PlaneLanding,
  scale: Scale,
  sparkles: Sparkles,
  target: Target,
  trophy: Trophy,
  zap: Zap,
  briefcase: Briefcase,
};

/** Resolve a passport icon name → component, or `undefined` if unknown. */
export function resolvePassportIcon(name: string | undefined | null): LucideIcon | undefined {
  return name ? PASSPORT_ICONS[name.trim()] : undefined;
}

/** Badge icon resolver — always returns a component (Award fallback). */
export function getPassportBadgeIcon(name: string | undefined | null): LucideIcon {
  return resolvePassportIcon(name) ?? Award;
}

/** Rarity → tailwind styling (border, glow, icon background, text). */
export const RARITY_STYLES: Record<
  PassportRarity,
  { border: string; glow: string; bg: string; text: string }
> = {
  common: {
    border: 'border-tone-slate-line',
    glow: '',
    bg: 'bg-tone-slate-subtle',
    text: 'text-tone-slate',
  },
  uncommon: {
    border: 'border-tone-green-solid',
    glow: 'shadow-[0_0_8px_rgba(34,197,94,0.3)]',
    bg: 'bg-tone-green-subtle',
    text: 'text-tone-green',
  },
  rare: {
    border: 'border-tone-blue-solid',
    glow: 'shadow-[0_0_8px_rgba(59,130,246,0.3)]',
    bg: 'bg-tone-blue-subtle',
    text: 'text-tone-blue',
  },
  epic: {
    border: 'border-tone-violet-solid',
    glow: 'shadow-[0_0_12px_rgba(147,51,234,0.4)]',
    bg: 'bg-tone-violet-subtle',
    text: 'text-tone-violet',
  },
  legendary: {
    border: 'border-tone-amber-solid',
    glow: 'shadow-[0_0_16px_rgba(245,158,11,0.5)]',
    bg: 'bg-tone-amber-subtle',
    text: 'text-tone-amber',
  },
};

// ─── Stamp card ────────────────────────────────────────────────────────────

export interface PassportStampCardProps {
  /** lucide icon NAME (e.g. "footprints") — falls back to rendering raw text if it isn't a known icon (e.g. a legacy emoji). */
  icon: string;
  name: string;
  rarity: PassportRarity;
  /** Translated rarity label, e.g. "Rare". */
  rarityLabel: string;
  unlocked: boolean;
  /** 0–100 progress for not-yet-earned stamps. */
  progress?: number;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function PassportStampCard({
  icon,
  name,
  rarity,
  rarityLabel,
  unlocked,
  progress,
  onClick,
  className,
  'data-testid': testId,
}: PassportStampCardProps): React.JSX.Element {
  const r = RARITY_STYLES[rarity];
  // Always render an icon — fall back to Award so a stamp with a missing/
  // unknown icon never shows a blank slot (keeps every card uniform).
  const Icon = resolvePassportIcon(icon) ?? Award;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        // `w-full` makes the card fill its grid cell (a <button> otherwise
        // shrinks to its content width → wider cards for longer names).
        // Fixed height + centered content keeps every card the SAME size.
        'relative w-full h-36 flex flex-col items-center justify-center p-4 rounded-xl transition-all border-2 cursor-pointer hover:scale-[1.03]',
        // Locked: convey it via the muted surface and a muted icon, NOT an
        // `opacity-60` on the whole card. Card-level opacity dims the TEXT too,
        // dropping even muted-foreground below WCAG AA contrast (axe flagged the
        // name + rarity label on /passport); lint refuses it now.
        unlocked ? cn('bg-card', r.border, r.glow) : 'bg-muted/50 border-muted',
        className
      )}
    >
      <Icon className={cn('w-8 h-8 mb-2', unlocked ? r.text : 'text-muted-foreground')} />
      {/* Fixed-height wrapper (centers) + inner clamp span. Keeping the flex
          and the line-clamp on SEPARATE elements avoids the `display` conflict;
          every name occupies exactly h-8 so all cards are the same height. */}
      <span className="h-8 w-full flex items-center justify-center">
        <span
          className={cn(
            'text-xs font-medium text-center leading-tight line-clamp-2',
            unlocked ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {name}
        </span>
      </span>
      {/* On a locked card's muted ground the vibrant rarity color (e.g. amber
          for legendary) fails WCAG AA contrast. Mirror the icon/name above —
          muted-foreground when locked. */}
      <Badge
        variant="outline"
        className={cn('mt-1.5 text-2xs px-1.5', unlocked ? r.text : 'text-muted-foreground')}
      >
        {rarityLabel}
      </Badge>
      {unlocked && (
        <BadgeCheck className="absolute top-1 right-1 w-4 h-4 text-primary-accessible" />
      )}
      {!unlocked && progress !== undefined && progress > 0 && (
        <div className="absolute bottom-1 left-1 right-1">
          <Progress aria-label={name} value={progress} className="h-1" />
        </div>
      )}
    </button>
  );
}

// ─── Badge card ──────────────────────────────────────────────────────────────

export interface PassportBadgeCardProps {
  /** Resolved icon node (consumer maps the lucide name / emoji). */
  icon: React.ReactNode;
  name: string;
  rarity: PassportRarity;
  rarityLabel: string;
  /**
   * The badge's colour (the API's hex, or a token such as `var(--tone-slate-solid)`):
   * the earned border, and a 12% wash behind the icon.
   */
  color: string;
  earned: boolean;
  /**
   * Progress toward earning: the 0–100 value for the bar, and its label as the
   * caller formats it in the reader's locale (e.g. `formatPercent(value)`).
   */
  progress?: { value: number; label: string };
  /** Translated miles line, e.g. "+1000 miles" (shown when earned). */
  milesText?: string;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function PassportBadgeCard({
  icon,
  name,
  rarity,
  rarityLabel,
  color,
  earned,
  progress,
  milesText,
  onClick,
  className,
  'data-testid': testId,
}: PassportBadgeCardProps): React.JSX.Element {
  const r = RARITY_STYLES[rarity];
  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className={cn(
        // Fixed height + w-full = every badge card the same size (fills its cell).
        'relative w-full h-48 overflow-hidden transition-all',
        earned ? cn('border-2', r.glow) : 'border-border/50',
        onClick && 'cursor-pointer hover:scale-[1.02]',
        className
      )}
      style={{ borderColor: earned ? color : undefined }}
    >
      <CardContent className="p-4 h-full flex flex-col items-center justify-center text-center gap-2">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `color-mix(in oklab, ${color} 12%, transparent)` }}
        >
          {icon}
        </div>
        {/* Fixed 2-line height so names of different lengths keep cards even. */}
        <div className="flex items-center justify-center gap-1.5 h-10">
          <span className="font-semibold text-sm leading-tight line-clamp-2">{name}</span>
          {!earned && <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
        </div>
        {/* An unearned card shows a lock; its rarity label mirrors the name
            color (muted-foreground) when unearned. */}
        <Badge
          variant="outline"
          className={cn('text-2xs px-1.5', earned ? r.text : 'text-muted-foreground')}
        >
          {rarityLabel}
        </Badge>
        {/* Fixed-height footer slot: miles (earned) / progress (locked) / empty. */}
        <div className="h-5 w-full flex items-center justify-center">
          {earned && milesText ? (
            <span className="text-xs text-muted-foreground">{milesText}</span>
          ) : !earned && progress !== undefined && progress.value > 0 ? (
            <div className="w-full flex items-center gap-2">
              <Progress aria-label={name} value={progress.value} className="h-1.5 flex-1" />
              <span className="text-2xs text-muted-foreground">{progress.label}</span>
            </div>
          ) : null}
        </div>
        {earned && (
          <BadgeCheck className="absolute top-1.5 right-1.5 w-4 h-4 text-primary-accessible" />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Level row ───────────────────────────────────────────────────────────────

export interface PassportLevelRowProps {
  level: number;
  name: string;
  /** Translated "Level" word. */
  levelLabel: string;
  /** Translated "Current" chip label. */
  currentLabel: string;
  /** Translated threshold line, e.g. "5000 Miles". */
  thresholdText: string;
  /** Translated remaining line for locked levels, e.g. "900 more Miles…". */
  remainingText?: string;
  achieved: boolean;
  current: boolean;
  className?: string;
  'data-testid'?: string;
}

export function PassportLevelRow({
  level,
  name,
  levelLabel,
  currentLabel,
  thresholdText,
  remainingText,
  achieved,
  current,
  className,
  'data-testid': testId,
}: PassportLevelRowProps): React.JSX.Element {
  return (
    <Card
      data-testid={testId}
      className={cn(
        'relative overflow-hidden transition-all',
        current
          ? 'border-2 border-primary shadow-[0_0_12px_rgba(147,51,234,0.25)]'
          : achieved
            ? 'border-border'
            : 'border-border/50',
        className
      )}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div
          className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center shrink-0',
            achieved ? 'bg-primary/15 text-primary-accessible' : 'bg-muted text-muted-foreground'
          )}
        >
          {achieved ? <Trophy className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {levelLabel} {level}
            </span>
            <h4 className="font-semibold text-sm">{name}</h4>
            {current && (
              <Badge variant="outline" className="text-2xs text-primary-accessible">
                {currentLabel}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{thresholdText}</p>
          {!achieved && remainingText && (
            <p className="text-xs text-muted-foreground mt-0.5">{remainingText}</p>
          )}
        </div>
        {achieved && <CircleCheck className="w-5 h-5 text-primary-accessible shrink-0" />}
      </CardContent>
    </Card>
  );
}
